import React, { useState, useRef, useEffect } from 'react';
import { askAstrologer, isGeminiReady } from '../services/geminiService';
import { useLanguage } from '../context/LanguageContext';

export default function AskAstrologer() {
  const { language, t } = useLanguage();
  const [messages, setMessages] = useState([
    { role: 'ai', text: t('askGreeting') }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Reset greeting when language changes
  useEffect(() => {
    setMessages([{ role: 'ai', text: t('askGreeting') }]);
  }, [language, t]);

  const handleSend = async () => {
    const question = input.trim();
    if (!question || loading) return;
    if (!isGeminiReady()) {
      setMessages(prev => [...prev, { role: 'user', text: question }, { role: 'ai', text: t('apiKeyChat') }]);
      setInput('');
      return;
    }

    setMessages(prev => [...prev, { role: 'user', text: question }]);
    setInput('');
    setLoading(true);

    try {
      // Build context from last few messages
      const context = messages.slice(-6).map(m => `${m.role === 'user' ? 'User' : 'Astrologer'}: ${m.text}`).join('\n');
      const result = await askAstrologer(question, context, language);
      setMessages(prev => [...prev, { role: 'ai', text: result }]);
    } catch (err) {
      setMessages(prev => [...prev, { role: 'ai', text: `❌ ${err.message || 'Something went wrong. Please try again.'}` }]);
    } finally {
      setLoading(false);
    }
  };

  const suggestedQuestions = [
    t('sugQ1'), t('sugQ2'), t('sugQ3'),
    t('sugQ4'), t('sugQ5'), t('sugQ6'),
  ];

  return (
    <div className="page">
      <div className="container">
        <div className="page-header">
          <h2>{t('askTitle')}</h2>
          <p>{t('askDesc')}</p>
        </div>

        <div className="chat-container">
          {/* Suggested Questions */}
          {messages.length <= 1 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1.5rem', justifyContent: 'center' }}>
              {suggestedQuestions.map((q, i) => (
                <button
                  key={i}
                  className="tab"
                  onClick={() => { setInput(q); }}
                  style={{ fontSize: '0.8rem' }}
                >
                  {q}
                </button>
              ))}
            </div>
          )}

          {/* Chat Messages */}
          <div className="card" style={{ padding: '0' }}>
            <div className="chat-messages">
              {messages.map((msg, i) => (
                <div key={i} className={`chat-message ${msg.role}`}>
                  {msg.text}
                </div>
              ))}
              {loading && (
                <div className="chat-message ai" style={{ fontStyle: 'italic', color: 'var(--text-muted)' }}>
                  {t('askLoading')}
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input Area */}
            <div className="chat-input-area" style={{ padding: '1rem', borderTop: '1px solid var(--border-color)' }}>
              <input
                type="text"
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSend()}
                placeholder={t('askPlaceholder')}
                disabled={loading}
              />
              <button className="btn btn-primary" onClick={handleSend} disabled={loading || !input.trim()}>
                {loading ? '...' : '✨'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
